/**
 * RFC 7636 PKCE helpers.
 *
 * Generates a cryptographically random `code_verifier` and the matching
 * `code_challenge = base64url(sha256(verifier))`. Used for the
 * `Authorization Code with PKCE` flow against ZITADEL's `z01-web`
 * client (no client secret).
 *
 * Verifier length is fixed at 64 chars to stay safely within the
 * 43–128 spec window while remaining short enough to fit comfortably
 * in a session-storage value.
 */

const VERIFIER_BYTES = 48; // 48 bytes → 64 base64url chars.

export function generateCodeVerifier(): string {
  const bytes = new Uint8Array(VERIFIER_BYTES);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

export async function deriveCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(hash));
}

export function generateState(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

function base64UrlEncode(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) {
    s += String.fromCharCode(b);
  }
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
